import React, { useState, useEffect, useCallback, useRef } from 'react';
import './PayrollBoard.css';
import { ProfitAnalysisService } from '../services/profitAnalysisService';
import {
  DollarSign,
  Plus,
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  RefreshCw,
  Upload,
  X,
  Check
} from 'lucide-react';

const formatCurrency = (value) => {
  if (value === null || value === undefined || value === '') return '';
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(num)) return '';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(num);
};

const formatDateShort = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString + 'T00:00:00');
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const PayrollBoard = () => {
  const [employees, setEmployees] = useState([]);
  const [payroll, setPayroll] = useState([]);
  const [periods, setPeriods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [editingCell, setEditingCell] = useState(null); // { employeeId, periodKey }
  const [editValue, setEditValue] = useState('');
  const [showAddPeriod, setShowAddPeriod] = useState(false);
  const [newPeriodStart, setNewPeriodStart] = useState('');
  const [newPeriodEnd, setNewPeriodEnd] = useState('');
  const [visiblePeriodCount, setVisiblePeriodCount] = useState(6);
  const [periodOffset, setPeriodOffset] = useState(0);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importPreview, setImportPreview] = useState(null);
  const [importing, setImporting] = useState(false);
  const fileInputRef = useRef(null);
  const editInputRef = useRef(null);

  // Load data on mount
  useEffect(() => {
    loadData();
  }, []);

  // Focus input when editing starts
  useEffect(() => {
    if (editingCell && editInputRef.current) {
      editInputRef.current.focus();
      editInputRef.current.select();
    }
  }, [editingCell]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [employeesData, payrollData] = await Promise.all([
        ProfitAnalysisService.getEmployees(),
        ProfitAnalysisService.getAllPayroll()
      ]);

      setEmployees(employeesData);
      setPayroll(payrollData);

      // Extract unique periods sorted by end date (most recent last)
      const periodMap = new Map();
      payrollData.forEach(p => {
        const key = `${p.payPeriodStart}|${p.payPeriodEnd}`;
        if (!periodMap.has(key)) {
          periodMap.set(key, {
            key,
            start: p.payPeriodStart,
            end: p.payPeriodEnd
          });
        }
      });

      const sortedPeriods = Array.from(periodMap.values())
        .sort((a, b) => a.end.localeCompare(b.end));

      setPeriods(sortedPeriods);

      // Set offset to show most recent periods
      if (sortedPeriods.length > visiblePeriodCount) {
        setPeriodOffset(sortedPeriods.length - visiblePeriodCount);
      }

      // Suggest next period dates
      if (sortedPeriods.length > 0) {
        const lastPeriod = sortedPeriods[sortedPeriods.length - 1];
        const nextStart = new Date(lastPeriod.end + 'T00:00:00');
        nextStart.setDate(nextStart.getDate() + 1);
        const nextEnd = new Date(nextStart);
        nextEnd.setDate(nextStart.getDate() + 13); // 2 weeks
        setNewPeriodStart(nextStart.toISOString().split('T')[0]);
        setNewPeriodEnd(nextEnd.toISOString().split('T')[0]);
      } else {
        // Default to current 2-week period
        const today = new Date();
        const dayOfWeek = today.getDay();
        const daysUntilSaturday = (6 - dayOfWeek + 7) % 7;
        const endDate = new Date(today);
        endDate.setDate(today.getDate() + daysUntilSaturday);
        const startDate = new Date(endDate);
        startDate.setDate(endDate.getDate() - 13);
        setNewPeriodStart(startDate.toISOString().split('T')[0]);
        setNewPeriodEnd(endDate.toISOString().split('T')[0]);
      }
    } catch (err) {
      console.error('Error loading payroll data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Build lookup for quick access: { `${employeeId}|${periodKey}`: payrollRecord }
  const payrollLookup = useCallback(() => {
    const lookup = {};
    payroll.forEach(p => {
      const key = `${p.employeeId}|${p.payPeriodStart}|${p.payPeriodEnd}`;
      lookup[key] = p;
    });
    return lookup;
  }, [payroll]);

  // Get visible periods based on offset
  const visiblePeriods = periods.slice(periodOffset, periodOffset + visiblePeriodCount);

  // Navigate periods
  const canGoBack = periodOffset > 0;
  const canGoForward = periodOffset + visiblePeriodCount < periods.length;

  const goBack = () => {
    if (canGoBack) {
      setPeriodOffset(Math.max(0, periodOffset - visiblePeriodCount));
    }
  };

  const goForward = () => {
    if (canGoForward) {
      setPeriodOffset(Math.min(periods.length - visiblePeriodCount, periodOffset + visiblePeriodCount));
    }
  };

  // Cell editing
  const startEditing = (employeeId, period) => {
    const lookup = payrollLookup();
    const key = `${employeeId}|${period.start}|${period.end}`;
    const existing = lookup[key];
    setEditingCell({ employeeId, periodKey: period.key });
    setEditValue(existing ? existing.grossPay.toString() : '');
  };

  const cancelEditing = () => {
    setEditingCell(null);
    setEditValue('');
  };

  const saveEdit = async () => {
    if (!editingCell) return;

    const [periodStart, periodEnd] = editingCell.periodKey.split('|');
    const lookup = payrollLookup();
    const key = `${editingCell.employeeId}|${periodStart}|${periodEnd}`;
    const existing = lookup[key];

    const newValue = editValue.trim();
    const numValue = parseFloat(newValue);

    // Handle delete (empty or 0)
    if (!newValue || numValue === 0) {
      if (existing) {
        try {
          setSaving(true);
          await ProfitAnalysisService.deletePayroll(existing.id);
          await loadData();
        } catch (err) {
          console.error('Error deleting payroll:', err);
          setError(err.message);
        } finally {
          setSaving(false);
        }
      }
      setEditingCell(null);
      setEditValue('');
      return;
    }

    // Validate number
    if (isNaN(numValue) || numValue < 0) {
      setError('Please enter a valid positive number');
      return;
    }

    try {
      setSaving(true);
      setError(null);

      if (existing) {
        // Update existing record
        await ProfitAnalysisService.updatePayroll(existing.id, {
          payPeriodStart: periodStart,
          payPeriodEnd: periodEnd,
          grossPay: numValue,
          notes: existing.notes
        });
      } else {
        // Create new record
        await ProfitAnalysisService.createPayroll({
          employeeId: editingCell.employeeId,
          payPeriodStart: periodStart,
          payPeriodEnd: periodEnd,
          grossPay: numValue,
          notes: 'Manual entry'
        });
      }

      await loadData();
      setEditingCell(null);
      setEditValue('');
    } catch (err) {
      console.error('Error saving payroll:', err);
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      saveEdit();
    } else if (e.key === 'Escape') {
      cancelEditing();
    } else if (e.key === 'Tab') {
      e.preventDefault();
      // Move to next cell
      saveEdit();
    }
  };

  // Add new period
  const handleAddPeriod = () => {
    if (!newPeriodStart || !newPeriodEnd) return;

    // Check if period already exists
    const key = `${newPeriodStart}|${newPeriodEnd}`;
    if (periods.some(p => p.key === key)) {
      setError('This pay period already exists');
      return;
    }

    // Add to periods list
    const newPeriod = { key, start: newPeriodStart, end: newPeriodEnd };
    const newPeriods = [...periods, newPeriod].sort((a, b) => a.end.localeCompare(b.end));
    setPeriods(newPeriods);

    // Update offset to show the new period
    if (newPeriods.length > visiblePeriodCount) {
      setPeriodOffset(newPeriods.length - visiblePeriodCount);
    }

    // Suggest next period
    const nextStart = new Date(newPeriodEnd + 'T00:00:00');
    nextStart.setDate(nextStart.getDate() + 1);
    const nextEnd = new Date(nextStart);
    nextEnd.setDate(nextStart.getDate() + 13);
    setNewPeriodStart(nextStart.toISOString().split('T')[0]);
    setNewPeriodEnd(nextEnd.toISOString().split('T')[0]);

    setShowAddPeriod(false);
  };

  // Calculate totals
  const calculatePeriodTotal = (period) => {
    const lookup = payrollLookup();
    let total = 0;
    employees.forEach(emp => {
      const key = `${emp.id}|${period.start}|${period.end}`;
      const record = lookup[key];
      if (record && !isNaN(record.grossPay)) {
        total += record.grossPay;
      }
    });
    return total;
  };

  const calculateEmployeeTotal = (employeeId) => {
    return payroll
      .filter(p => p.employeeId === employeeId && !isNaN(p.grossPay))
      .reduce((sum, p) => sum + p.grossPay, 0);
  };

  const calculateGrandTotal = () => {
    return payroll
      .filter(p => !isNaN(p.grossPay))
      .reduce((sum, p) => sum + p.grossPay, 0);
  };

  // CSV Import handlers
  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target?.result;
      if (typeof text === 'string') {
        parseCSV(text);
      }
    };
    reader.readAsText(file);
  };

  const parseCSV = (text) => {
    const lines = text.split('\n');
    if (lines.length < 2) {
      setError('CSV file is empty or invalid');
      return;
    }

    const parseRow = (line) => {
      const values = [];
      let current = '';
      let inQuotes = false;

      for (let i = 0; i < line.length; i++) {
        const char = line[i];
        if (char === '"') {
          inQuotes = !inQuotes;
        } else if (char === ',' && !inQuotes) {
          values.push(current.trim());
          current = '';
        } else {
          current += char;
        }
      }
      values.push(current.trim());
      return values;
    };

    const headers = parseRow(lines[0]);
    const headerMap = {};
    headers.forEach((h, i) => {
      const lower = h.toLowerCase().replace(/[^a-z0-9]/g, '');
      if (lower.includes('payperiod')) headerMap.payPeriod = i;
      else if (lower.includes('employeefirst')) headerMap.employeeFirstName = i;
      else if (lower.includes('employeelast')) headerMap.employeeLastName = i;
      else if (lower === 'grosspay') headerMap.grossPay = i;
      else if (lower.includes('employermedicare')) headerMap.employerMedicareTax = i;
      else if (lower.includes('employersocialsecurity')) headerMap.employerSocialSecurity = i;
      else if (lower.includes('source')) headerMap.source = i;
      else if (lower.includes('unemployment')) headerMap.stateUnemployment = i;
    });

    const rows = [];
    for (let i = 1; i < lines.length; i++) {
      const line = lines[i].trim();
      if (!line) continue;

      const values = parseRow(line);
      const row = {
        payPeriod: values[headerMap.payPeriod] || '',
        employeeFirstName: values[headerMap.employeeFirstName] || '',
        employeeLastName: values[headerMap.employeeLastName] || '',
        grossPay: values[headerMap.grossPay] || '0',
        employerMedicareTax: values[headerMap.employerMedicareTax] || '0',
        employerSocialSecurity: values[headerMap.employerSocialSecurity] || '0',
        source: values[headerMap.source] || '',
        stateUnemployment: values[headerMap.stateUnemployment] || '0'
      };

      if (row.source.toLowerCase().includes('void')) continue;
      if (!row.employeeFirstName && !row.employeeLastName) continue;

      rows.push(row);
    }

    setImportPreview({
      totalRows: rows.length,
      rows: rows.slice(0, 10),
      allRows: rows
    });
    setShowImportModal(true);
  };

  const handleImport = async () => {
    if (!importPreview?.allRows) return;

    try {
      setImporting(true);
      const results = await ProfitAnalysisService.importPayrollFromCSV(
        importPreview.allRows,
        employees
      );

      alert(`Import complete!\n\nImported: ${results.imported}\nSkipped: ${results.skipped}${results.errors.length > 0 ? `\n\nErrors:\n${results.errors.slice(0, 5).join('\n')}${results.errors.length > 5 ? `\n... and ${results.errors.length - 5} more` : ''}` : ''}`);

      setShowImportModal(false);
      setImportPreview(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      loadData();
    } catch (err) {
      console.error('Error importing payroll:', err);
      setError(`Import error: ${err.message}`);
    } finally {
      setImporting(false);
    }
  };

  if (loading) {
    return (
      <div className="payroll-container">
        <div className="payroll-card">
          <div className="payroll-loading">
            <RefreshCw className="h-6 w-6 animate-spin" style={{ color: '#2563EB' }} />
            <span>Loading payroll data...</span>
          </div>
        </div>
      </div>
    );
  }

  const lookup = payrollLookup();

  return (
    <div className="payroll-container">
      <div className="payroll-card">
        {/* Header */}
        <div className="payroll-header">
          <div className="payroll-title">
            <DollarSign className="h-6 w-6" />
            <h1>Payroll</h1>
          </div>

          <div className="payroll-actions">
            {/* Period Navigation */}
            <div className="payroll-nav">
              <button
                onClick={goBack}
                disabled={!canGoBack}
                className="btn btn-secondary"
                title="Previous periods"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <span className="payroll-nav-label">
                {visiblePeriods.length > 0 ? (
                  `${formatDateShort(visiblePeriods[0]?.start)} - ${formatDateShort(visiblePeriods[visiblePeriods.length - 1]?.end)}`
                ) : 'No periods'}
              </span>
              <button
                onClick={goForward}
                disabled={!canGoForward}
                className="btn btn-secondary"
                title="Next periods"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>

            <div className="payroll-actions-group">
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                onChange={handleFileSelect}
                style={{ display: 'none' }}
              />
              <button
                onClick={() => fileInputRef.current?.click()}
                className="btn btn-secondary"
              >
                <Upload className="h-4 w-4" />
                Import CSV
              </button>
              <button
                onClick={() => setShowAddPeriod(true)}
                className="btn btn-primary"
              >
                <Plus className="h-4 w-4" />
                Add Period
              </button>
            </div>
          </div>
        </div>

        {/* Error State */}
        {error && (
          <div className="payroll-error">
            <AlertCircle className="h-5 w-5" />
            <p>{error}</p>
            <button onClick={() => setError(null)} className="payroll-error-close">
              <X className="h-4 w-4" />
            </button>
          </div>
        )}

        {/* Spreadsheet Grid */}
        <div className="payroll-table-wrapper">
          <table className="payroll-table payroll-grid">
            <thead className="payroll-thead">
              <tr>
                <th className="payroll-cell-employee">Employee</th>
                {visiblePeriods.map(period => (
                  <th key={period.key} className="payroll-cell-period">
                    <div className="period-header">
                      <span className="period-dates">
                        {formatDateShort(period.start)} - {formatDateShort(period.end)}
                      </span>
                    </div>
                  </th>
                ))}
                <th className="payroll-cell-total">Total</th>
              </tr>
            </thead>
            <tbody className="payroll-tbody">
              {employees.map(employee => (
                <tr key={employee.id} className="payroll-row">
                  <td className="payroll-cell payroll-cell-employee">
                    <div className="employee-info">
                      <span className="employee-name">{employee.name}</span>
                      {employee.is_contractor && (
                        <span className="chip chip--contractor">Contractor</span>
                      )}
                    </div>
                  </td>
                  {visiblePeriods.map(period => {
                    const cellKey = `${employee.id}|${period.start}|${period.end}`;
                    const record = lookup[cellKey];
                    const isEditing = editingCell?.employeeId === employee.id &&
                                     editingCell?.periodKey === period.key;

                    return (
                      <td
                        key={period.key}
                        className={`payroll-cell payroll-cell-amount ${isEditing ? 'editing' : ''} ${record ? 'has-value' : ''}`}
                        onClick={() => !isEditing && startEditing(employee.id, period)}
                      >
                        {isEditing ? (
                          <div className="cell-edit">
                            <span className="cell-edit-prefix">$</span>
                            <input
                              ref={editInputRef}
                              type="text"
                              inputMode="decimal"
                              value={editValue}
                              onChange={(e) => setEditValue(e.target.value)}
                              onKeyDown={handleKeyDown}
                              onBlur={saveEdit}
                              className="cell-edit-input"
                              disabled={saving}
                            />
                          </div>
                        ) : (
                          <span className="cell-value">
                            {record && !isNaN(record.grossPay)
                              ? formatCurrency(record.grossPay)
                              : ''}
                          </span>
                        )}
                      </td>
                    );
                  })}
                  <td className="payroll-cell payroll-cell-total">
                    <span className="cell-total">
                      {formatCurrency(calculateEmployeeTotal(employee.id))}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot className="payroll-totals">
              <tr>
                <td className="payroll-cell payroll-cell-employee">
                  <strong>TOTALS</strong>
                </td>
                {visiblePeriods.map(period => (
                  <td key={period.key} className="payroll-cell payroll-cell-amount">
                    <span className="cell-total">
                      {formatCurrency(calculatePeriodTotal(period))}
                    </span>
                  </td>
                ))}
                <td className="payroll-cell payroll-cell-total">
                  <span className="cell-grand-total">
                    {formatCurrency(calculateGrandTotal())}
                  </span>
                </td>
              </tr>
            </tfoot>
          </table>
        </div>

        {/* Period count info */}
        <div className="payroll-footer">
          <span className="payroll-info">
            {periods.length} pay period{periods.length !== 1 ? 's' : ''} •
            {employees.length} employee{employees.length !== 1 ? 's' : ''}
          </span>
        </div>
      </div>

      {/* Add Period Modal */}
      {showAddPeriod && (
        <div className="modal-overlay" onClick={() => setShowAddPeriod(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Add Pay Period</h3>
              <button className="modal-close" onClick={() => setShowAddPeriod(false)}>
                <X size={20} />
              </button>
            </div>

            <div className="modal-body">
              <div className="form-row">
                <div className="form-group">
                  <label>Period Start</label>
                  <input
                    type="date"
                    value={newPeriodStart}
                    onChange={(e) => setNewPeriodStart(e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label>Period End</label>
                  <input
                    type="date"
                    value={newPeriodEnd}
                    onChange={(e) => setNewPeriodEnd(e.target.value)}
                  />
                </div>
              </div>
              <p className="form-hint">
                This creates empty columns for the period. Click cells to enter amounts.
              </p>
            </div>

            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowAddPeriod(false)}>
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={handleAddPeriod}
                disabled={!newPeriodStart || !newPeriodEnd}
              >
                <Check className="h-4 w-4" />
                Add Period
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Import Preview Modal */}
      {showImportModal && importPreview && (
        <div className="modal-overlay" onClick={() => !importing && setShowImportModal(false)}>
          <div className="modal-content modal-wide" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Import Payroll from CSV</h3>
              <button className="modal-close" onClick={() => !importing && setShowImportModal(false)} disabled={importing}>
                <X size={20} />
              </button>
            </div>

            <div className="modal-body">
              <p className="import-summary">
                Found <strong>{importPreview.totalRows}</strong> payroll entries to import.
                Preview of first {Math.min(10, importPreview.rows.length)} rows:
              </p>

              <table className="import-preview-table">
                <thead>
                  <tr>
                    <th>Employee</th>
                    <th>Pay Period</th>
                    <th>Gross Pay</th>
                    <th>Employer Taxes</th>
                  </tr>
                </thead>
                <tbody>
                  {importPreview.rows.map((row, idx) => {
                    const parseMoney = (val) => parseFloat((val || '0').replace(/[$,]/g, '')) || 0;
                    const gross = parseMoney(row.grossPay);
                    const taxes = parseMoney(row.employerMedicareTax) + parseMoney(row.employerSocialSecurity) + parseMoney(row.stateUnemployment);
                    return (
                      <tr key={idx}>
                        <td>{row.employeeFirstName} {row.employeeLastName}</td>
                        <td>{row.payPeriod}</td>
                        <td>{formatCurrency(gross)}</td>
                        <td>{formatCurrency(taxes)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              <p className="import-note">
                Duplicates will be automatically skipped. Voided paychecks are excluded.
              </p>
            </div>

            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowImportModal(false)} disabled={importing}>
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={handleImport}
                disabled={importing}
              >
                {importing ? 'Importing...' : `Import ${importPreview.totalRows} Entries`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PayrollBoard;
